import { createApi } from '@reduxjs/toolkit/query/react';
import { setFilesDescription } from '@store/slices/files-description.slice';
import {
  FOFilesDescriptionInterface,
  FOFilesInterface,
  FOUploadFilesPayloadInterface,
  IFileDeletePayload,
  IFileDownloadMultiplePayload,
  IFileEncryptPayload,
  IFileEncryptResponse,
} from '../interfaces/files.interface';
import { baseQuery } from '../services/baseQuery';
import { isHydrateAction } from '../services/hydration';
import { persistKey } from '../store';
import { handleError } from '../utils/api-error-handling';
import { isValidUUID } from '../utils/format';
import { encryptPayload } from '../utils/security';

export const filesApi = createApi({
  reducerPath: 'filesApi',
  baseQuery: baseQuery,
  extractRehydrationInfo(action, { reducerPath }): any {
    try {
      if (isHydrateAction(action)) {
        if (
          action.key === persistKey &&
          action.payload &&
          action.payload[reducerPath]
        ) {
          return action.payload[reducerPath];
        }
      }
    } catch (error) {
      console.error('Error extracting rehydration info:', error);
    }
    return undefined;
  },
  tagTypes: ['Files', 'Files Description'],
  endpoints: (builder) => ({
    getFiles: builder.query({
      query: (id: string | string[]) => {
        if (Array.isArray(id)) {
          return `files?fileIDs[ids]=${id}`;
        } else if (isValidUUID(id)) {
          return `files/${id}`;
        } else {
          throw new Error('Invalid UUID');
        }
      },
      providesTags: ['Files'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        await queryFulfilled;
      },
      keepUnusedDataFor: 0,
      forceRefetch() {
        return true;
      },
      transformResponse: (baseQueryReturnValue: FOFilesInterface) => {
        return baseQueryReturnValue?.data?.files?.map((item) => item.file);
      },
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    getFilesDescription: builder.query({
      query: () => 'lists/files-descriptions',
      providesTags: ['Files Description'],
      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          const {
            data: baseQueryReturnValue,
          }: {
            data: FOFilesDescriptionInterface;
          } = await queryFulfilled;

          if (!baseQueryReturnValue) return;

          const returnItem = baseQueryReturnValue?.data?.descriptions
            ?.map((item) => item.description.description)
            ?.sort((a, b) => a.localeCompare(b));

          if (!returnItem) return;

          dispatch(setFilesDescription(returnItem));
        } catch (error) {}
      },
      keepUnusedDataFor: 0,
      forceRefetch() {
        return true;
      },
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    getDownloadFile: builder.query<Blob, { id: string; validity?: number }>({
      query: ({ id, validity }: { id: string; validity?: number }) => ({
        url: `files/${id}/download${validity ? `/${validity}` : ''}`,
        responseHandler: (response) => response.blob(), // Ensure the response is a Blob
      }),
      providesTags: ['Files'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        await queryFulfilled;
      },
      keepUnusedDataFor: 0,
      forceRefetch() {
        return true;
      },
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),

    getDownloadMultipleFile: builder.query<Blob, IFileDownloadMultiplePayload>({
      query: (fileIDs: IFileDownloadMultiplePayload) => ({
        url: `files/download-multiple`,
        method: 'POST',
        body: encryptPayload(fileIDs),
        responseHandler: (response) => response.blob(), // Ensure the response is a Blob
      }),
      providesTags: ['Files'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        await queryFulfilled;
      },
      keepUnusedDataFor: 0,
      forceRefetch() {
        return true;
      },
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),

    postUploadFiles: builder.mutation({
      query: (payload: FOUploadFilesPayloadInterface) => ({
        url: 'files',
        method: 'POST',
        body: encryptPayload(payload),
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(filesApi.util.invalidateTags(['Files']));
          }
        } catch (error) {}
      },
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    deleteFiles: builder.mutation({
      query: (payload: IFileDeletePayload) => ({
        url: 'files',
        method: 'DELETE',
        body: encryptPayload(payload),
      }),
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    encryptFile: builder.mutation({
      query: (payload: IFileEncryptPayload) => ({
        url: 'files/encrypt',
        method: 'POST',
        body: encryptPayload(payload),
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(filesApi.util.invalidateTags(['Files']));
          }
        } catch (error) {}
      },
      transformResponse(response: IFileEncryptResponse) {
        return response.data[0].encryptedData;
      },
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
    }),
  }),
});

export const {
  useGetFilesQuery,
  useLazyGetDownloadFileQuery,
  usePostUploadFilesMutation,
  useDeleteFilesMutation,
  useGetFilesDescriptionQuery,
  useEncryptFileMutation,
  useLazyGetDownloadMultipleFileQuery,
  useLazyGetFilesQuery,
} = filesApi;
